import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  RESUME_ERROR_MESSAGE_MAX,
  RESUME_ERROR_NAME_MAX,
  type BoundaryErrorReport,
} from '@chat-emp/shared';

import { reportBoundaryError } from '../lib/facade.js';
import { captureError } from '../lib/replay.js';

interface ErrorBoundaryProps {
  /** Shown in place of the subtree when a render throws. */
  fallback: ReactNode;
  /** Names this boundary in the crash report sent to our logs. */
  where: BoundaryErrorReport['where'];
  children: ReactNode;
}

interface ErrorBoundaryState {
  failed: boolean;
}

/**
 * Contains render errors to a subtree. A crash here — e.g. a component tripping
 * over dirty upstream data — swaps that subtree for `fallback` instead of
 * blanking the whole app. Scope it around the riskiest surfaces (the details
 * panel, which renders unvalidated candidate data), not the whole tree, so one
 * bad field never takes the page down.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[chat-emp] render error contained by boundary', error, info.componentStack);
    void reportBoundaryError({
      where: this.props.where,
      errorName: String(error?.name ?? '').slice(0, RESUME_ERROR_NAME_MAX),
      errorMessage: String(error?.message ?? error).slice(0, RESUME_ERROR_MESSAGE_MAX),
    });
    captureError(error);
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
